import type { components } from "@appretio/api";
import { Option } from "@appretio/ui-kit";
import type { ForecastMetaDataType } from "../../hooks/useFetchForecastMetaData";
import type {
  ForecastTableControlOptions,
  ForecastTableControlValues
} from "./forecastTableControlValues";

/**
 * The years a planner may anchor on, as offsets from the current one.
 *
 * Both bounds are product choices — the tenant calendar serves a far wider
 * span, and a planner reaches back further than they plan forward. One range
 * for both calendars, so switching view relabels the years.
 */
const PLANNING_YEAR_OFFSETS = { from: -3, to: 2 };

/** Where the range puts the current year, for the lists built off it. */
const CURRENT_YEAR_INDEX = -PLANNING_YEAR_OFFSETS.from;

/** The offsets the range covers, ascending. */
function planningYearRange(): number[] {
  const { from, to } = PLANNING_YEAR_OFFSETS;
  return Array.from({ length: to - from + 1 }, (_, index) => from + index);
}

/** The in-range years of a year-to-offset mapping, ascending. */
function planningYearsFromMapping(mapping: Record<string, number>): Option[] {
  return Object.entries(mapping)
    .filter(
      ([, offset]) =>
        offset >= PLANNING_YEAR_OFFSETS.from &&
        offset <= PLANNING_YEAR_OFFSETS.to
    )
    .sort(([, a], [, b]) => a - b)
    .map(([year]) => ({ label: year, value: year }));
}

/**
 * Generates fiscal planning year options in "YYYY/YYYY" format.
 *
 * Empty without the mapping. Which "YYYY/YYYY" the tenant is currently in
 * turns on its `fiscal_year_start_month`, which is dbt tenant config and
 * reaches no wire, so a clock names the fiscal year only by luck of the month.
 */
export function getFiscalPlanningYears(
  yearMapping?: Record<string, number>
): Option[] {
  return yearMapping ? planningYearsFromMapping(yearMapping) : [];
}

/**
 * Generates calendar year planning year options in "YYYY" format.
 *
 * Off the tenant calendar where the metadata has landed, off the clock while
 * the query is in flight — a calendar year needs no tenant input to name.
 */
export function getCalendarYearPlanningYears(
  gregYearMapping?: Record<string, number>
): Option[] {
  if (gregYearMapping) return planningYearsFromMapping(gregYearMapping);

  const currentYear = new Date().getFullYear();
  return planningYearRange().map((offset) => {
    const label = String(currentYear + offset);
    return { label, value: label };
  });
}

/**
 * Planning-year options for a calendar view. "Greg cal year" labels them as
 * plain calendar years; the two fiscal views use "YYYY/YYYY".
 *
 * @param metaData - Grid metadata; supplies both year mappings. Omit while the
 *   query is in flight: the calendar years come off the clock, the fiscal ones
 *   come back empty.
 */
export function getPlanningYearOptions(
  calendarView: ForecastTableControlValues["calendarView"],
  metaData?: Pick<ForecastMetaDataType, "yearMapping" | "gregYearMapping">
): Option[] {
  return calendarView === "Greg cal year"
    ? getCalendarYearPlanningYears(metaData?.gregYearMapping)
    : getFiscalPlanningYears(metaData?.yearMapping);
}

/** The mapping a calendar view's planning-year options are counted off. */
function planningYearOffsets(
  calendarView: ForecastTableControlValues["calendarView"],
  metaData?: Pick<ForecastMetaDataType, "yearMapping" | "gregYearMapping">
): Record<string, number> | undefined {
  return calendarView === "Greg cal year"
    ? metaData?.gregYearMapping
    : metaData?.yearMapping;
}

/**
 * Returns the planning year at offset 0 — the one the tenant calendar is in.
 *
 * Resolved against the mapping where the metadata has landed and against
 * `PLANNING_YEAR_OFFSETS` otherwise, so widening the range carries the default
 * along with it.
 *
 * @param metaData - Grid metadata; omit while the query is in flight.
 */
export function getDefaultPlanningYear(
  calendarView: ForecastTableControlValues["calendarView"],
  metaData?: Pick<ForecastMetaDataType, "yearMapping" | "gregYearMapping">
): string {
  const options = getPlanningYearOptions(calendarView, metaData);
  const offsets = planningYearOffsets(calendarView, metaData);
  const current = offsets
    ? options.find((option) => offsets[option.value] === 0)
    : options[CURRENT_YEAR_INDEX];
  return current?.value ?? "";
}

/**
 * Default week-label display mode from the tenant's `week_label_basis`
 * setting: fiscal numbering (the W01…W53 column ids, W01 = first fiscal week)
 * or the calendar labels from `gregMapping`. `Calendar` while the settings load.
 */
export function defaultWeekLabelMode(
  basis: components["schemas"]["WeekLabelBasis"] | undefined
): ForecastTableControlValues["weekLabels"] {
  return basis === "fiscal" ? "Fiscal" : "Calendar";
}

/** Time periods each calendar family can express. */
const TIME_PERIOD_OPTIONS: Record<
  ForecastTableControlValues["calendarView"],
  ForecastTableControlValues["timePeriod"][]
> = {
  "Inv 454": ["WK", "MO", "QTR", "HY", "PTG"],
  "Fin greg": ["MO", "QTR", "HY"],
  "Greg cal year": ["MO", "QTR", "HY"]
};

/**
 * The option lists that follow the calendar family: weekly periods only
 * exist in the 454 calendar, and planning years are labelled per family.
 *
 * @param metaData - Grid metadata; omit while the query is in flight.
 */
export function calendarViewOptions(
  calendarView: ForecastTableControlValues["calendarView"],
  metaData?: Pick<ForecastMetaDataType, "yearMapping" | "gregYearMapping">
): Pick<ForecastTableControlOptions, "timePeriod" | "planningYear"> {
  return {
    timePeriod: TIME_PERIOD_OPTIONS[calendarView],
    planningYear: getPlanningYearOptions(calendarView, metaData)
  };
}

/**
 * The controls after switching calendar family: the period and planning
 * year preserved where they survive the switch, defaulted where they don't.
 */
export function controlsForCalendarView(
  calendarView: ForecastTableControlValues["calendarView"],
  values: ForecastTableControlValues,
  metaData?: Pick<ForecastMetaDataType, "yearMapping" | "gregYearMapping">
): ForecastTableControlValues {
  const { timePeriod, planningYear } = calendarViewOptions(
    calendarView,
    metaData
  );
  return {
    ...values,
    calendarView,
    timePeriod: timePeriod.includes(values.timePeriod)
      ? values.timePeriod
      : timePeriod[0],
    planningYear: planningYear.some((o) => o.value === values.planningYear)
      ? values.planningYear
      : getDefaultPlanningYear(calendarView, metaData)
  };
}

/**
 * Options and values after switching calendar family. Shared by the
 * calendar tabs and by applying a saved view, so a view lands on exactly
 * the state a click produces.
 *
 * @param metaData - Grid metadata; omit while the query is in flight.
 */
export function applyCalendarViewChange(
  calendarView: ForecastTableControlValues["calendarView"],
  values: ForecastTableControlValues,
  options: ForecastTableControlOptions,
  metaData?: Pick<ForecastMetaDataType, "yearMapping" | "gregYearMapping">
): {
  options: ForecastTableControlOptions;
  values: ForecastTableControlValues;
} {
  return {
    options: { ...options, ...calendarViewOptions(calendarView, metaData) },
    values: controlsForCalendarView(calendarView, values, metaData)
  };
}
